import {
  type InfiniteData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/vue-query";
import { computed, type MaybeRef, unref } from "vue";

import type { components as OpenAPI } from "@/autogen/openapi";
import { withExactVariables } from "@/lib/api/exact";
import { ok } from "@/lib/openapi";
import { useNewsletter } from "@/store/stores/account";

import api from "../../base";
import { createBatchedFetch } from "../batch-fetch";
import { useGenericListingQuery } from "../generic";
import { findItemInInfiniteQueries, useNewsletterId } from "../utils";
import type { SubscriberPage } from "./list";

export type Subscriber = OpenAPI["schemas"]["Subscriber"];

type SubscriberCreateInput = OpenAPI["schemas"]["SubscriberInput"];

type SubscriberUpdateInput = OpenAPI["schemas"]["SubscriberUpdateInput"] & {
  id: string;
};

const fetchSubscriber = createBatchedFetch<string, Subscriber>({
  limit: 50,
  idFromResource(resource) {
    return resource.id;
  },
  async fetch(ids, signal) {
    const data = await ok(
      api.get("/subscribers", {
        signal,
        params: {
          query: {
            ids: ids,
          },
        },
      })
    );

    return data.results;
  },
});

const fetchSubscriberDetail = async (id: string, signal: AbortSignal) => {
  return await ok(
    api.get("/subscribers/{id_or_email}", {
      signal,
      params: {
        path: { id_or_email: id },
        query: {},
      },
    })
  );
};

const SUBSCRIBER_DETAIL_STALE_TIME = 30000;

export const subscriberDetailQueryOptions = (
  newsletterId: string,
  id: string
) => {
  return queryOptions({
    queryKey: [newsletterId, "subscribers", "detail", id] as const,
    staleTime: SUBSCRIBER_DETAIL_STALE_TIME,
    queryFn: ({ signal }) => fetchSubscriberDetail(id, signal),
  });
};

export const useSubscriberDetailQuery = ({
  id,
  batch = false,
}: {
  id: MaybeRef<string | undefined>;
  batch?: boolean;
}) => {
  const queryClient = useQueryClient();
  const newsletterId = useNewsletterId();

  const query = useQuery({
    queryKey: [newsletterId, "subscribers", "detail", id],
    staleTime: SUBSCRIBER_DETAIL_STALE_TIME,
    enabled: () => unref(id) !== undefined,
    async queryFn({ signal }) {
      if (batch) {
        return await fetchSubscriber(unref(id)!, signal);
      }
      return await fetchSubscriberDetail(unref(id)!, signal);
    },
    placeholderData(): Subscriber | undefined {
      const needle = unref(id);

      {
        const queries = queryClient.getQueriesData<
          InfiniteData<SubscriberPage>
        >({
          queryKey: [newsletterId, "subscribers", "list"],
        });

        const found = findItemInInfiniteQueries<Subscriber>(
          queries,
          (item) => item.id === needle
        );

        if (found !== undefined) {
          return found;
        }
      }
    },
  });

  return query;
};

export const useSubscriberReferralsListingQuery = ({
  subscriberId,
  enabled = true,
}: {
  subscriberId: MaybeRef<string>;
  enabled?: MaybeRef<boolean>;
}) => {
  const newsletterId = useNewsletterId();
  const newsletter = useNewsletter();

  const isEnabled = computed(() => {
    if (!newsletter.value.enabled_features.includes("referrals")) {
      return false;
    }

    return unref(enabled);
  });

  const query = useGenericListingQuery({
    queryKey: [newsletterId, "subscribers", "referrals", subscriberId],
    enabled: isEnabled,
    async fetch({ page, signal }) {
      const data = await ok(
        api.get("/subscribers/{id_or_email}/referrals", {
          signal,
          params: {
            path: {
              id_or_email: unref(subscriberId),
            },
            query: {
              page: page,
            },
          },
        })
      );

      return data;
    },
  });

  return query;
};

export const useSubscriberStripeSubscriptionsListingQuery = ({
  subscriberId,
  enabled = true,
}: {
  subscriberId: MaybeRef<string>;
  enabled?: MaybeRef<boolean>;
}) => {
  const newsletterId = useNewsletterId();
  const newsletter = useNewsletter();

  const isEnabled = computed(() => {
    if (!newsletter.value.enabled_features.includes("paid_subscriptions")) {
      return false;
    }

    return unref(enabled);
  });

  const query = useGenericListingQuery({
    queryKey: [
      newsletterId,
      "subscribers",
      "stripe-subscriptions",
      subscriberId,
    ],
    enabled: isEnabled,
    async fetch({ page, signal }) {
      const data = await ok(
        api.get("/subscribers/{id_or_email}/stripe-subscriptions", {
          signal,
          params: {
            path: {
              id_or_email: unref(subscriberId),
            },
            query: {
              page: page,
            },
          },
        })
      );

      return {
        ...data,
        results: data.results.map((item) => {
          return { ...item, id: item.subscription_id };
        }),
      };
    },
  });

  return query;
};

export const useSubscriberAutomationsListingQuery = ({
  subscriberId,
  enabled = true,
}: {
  subscriberId: MaybeRef<string>;
  enabled?: MaybeRef<boolean>;
}) => {
  const newsletterId = useNewsletterId();
  const newsletter = useNewsletter();

  const isEnabled = computed(() => {
    if (!newsletter.value.enabled_features.includes("automations")) {
      return false;
    }

    return unref(enabled);
  });

  const query = useGenericListingQuery({
    queryKey: [newsletterId, "subscribers", "automations", subscriberId],
    enabled: isEnabled,
    async fetch({ page, signal }) {
      const data = await ok(
        api.get("/subscribers/{id_or_email}/automations", {
          signal,
          params: {
            path: {
              id_or_email: unref(subscriberId),
            },
            query: {
              page: page,
            },
          },
        })
      );

      return data;
    },
  });

  return query;
};

export const useCreateSubscriberMutation = () => {
  const queryClient = useQueryClient();
  const newsletterId = useNewsletterId();

  const mutation = useMutation({
    async mutationFn(input: SubscriberCreateInput): Promise<Subscriber> {
      const data = await ok(
        api.post("/subscribers", {
          body: input,
          headers: {
            "X-Buttondown-Bypass-Firewall": "true",
          },
        })
      );

      return data;
    },
    onSuccess(data) {
      queryClient.setQueryData(
        [newsletterId, "subscribers", "detail", data.id],
        data
      );

      queryClient.invalidateQueries({
        queryKey: [newsletterId, "subscribers"],
      });

      queryClient.invalidateQueries({
        queryKey: [newsletterId, "search"],
      });
    },
  });

  // Reject create bodies carrying keys outside SubscriberInput at compile time;
  // a spread can otherwise smuggle a form-only field past TS and 422 (B-4421).
  return withExactVariables<SubscriberCreateInput>()(mutation);
};

export const useUpdateSubscriberMutation = () => {
  const queryClient = useQueryClient();
  const newsletterId = useNewsletterId();

  const mutation = useMutation({
    async mutationFn({
      id,
      ...input
    }: SubscriberUpdateInput): Promise<Subscriber> {
      const data = await ok(
        api.patch("/subscribers/{id_or_email}", {
          params: {
            path: { id_or_email: id },
          },
          body: input,
        })
      );

      return data;
    },
    onSuccess(data) {
      queryClient.setQueryData(
        [newsletterId, "subscribers", "detail", data.id],
        data
      );

      queryClient.invalidateQueries({
        queryKey: [newsletterId, "subscribers"],
      });

      queryClient.invalidateQueries({
        queryKey: [newsletterId, "search"],
      });
    },
  });

  return mutation;
};
